import { Dispatch, useMemo, useReducer } from 'react';

import Cookie from 'js-cookie';
import { useIntl } from 'react-intl';

import { GOOGLE_AUTH_ID, COOKIE_ERROR_MESSAGE } from '../../../../../constants/Common';

import { IReducerAction, IReducerActionWithField } from '../../../../Hooks/Context/connect';

import getCustomStyle from '../CustomStyles';

import { IAuthFunctionalProps } from '../../../../modules/AuthFunctional/AuthFunctional';
import reducer, { initialState, IAuthState } from '../Store/Reducers';


type TUseCombinedReducersProps = IAuthFunctionalProps;

interface IUseCombinedReducersProps {
    dispatch: Dispatch<IReducerAction | IReducerActionWithField>;
}

const useCombinedReducers = (props: TUseCombinedReducersProps) => {
    const intl = useIntl();

    const theme = props.theme || 'light';
    const customStyleProp = props.customStyle;
    const useUSFormat = props.lang === 'en_us';
    const dateFormat = useUSFormat ? 'MM/DD/YYYY' : 'DD/MM/YYYY';

    const customStyle = getCustomStyle(customStyleProp);

    const communication = useMemo(() => {
        let newCommunication = { ...customStyle.communication };

        if (customStyle.needConversion) {
            Object.keys(newCommunication).forEach((key) => {
                const id = newCommunication[key];

                // Tema sem a chave no locale ativo fica vazio; `formatMessage` devolveria o id cru.
                if (!id || !intl.messages?.[id]) return;

                newCommunication = {
                    ...newCommunication,
                    [key]: intl.formatMessage({ id })
                };
            });
        }

        return newCommunication;
    }, [intl, customStyle]);

    const oauthErrorMessage = useMemo(() => {
        const message = Cookie.get(COOKIE_ERROR_MESSAGE);
        const metaMessage = document.querySelector<HTMLMetaElement>(`meta[name=${COOKIE_ERROR_MESSAGE}]`);

        if (!message) return undefined;

        if (metaMessage) return metaMessage.content;

        const normalizedMessage = message.replace(/\+/g, ' ');
        const meta = document.createElement('meta');

        meta.name = COOKIE_ERROR_MESSAGE;
        meta.content = normalizedMessage;
        document.head.appendChild(meta);

        Cookie.remove(COOKIE_ERROR_MESSAGE);
        return normalizedMessage;
    }, []);

    const oauthShowConsetTerms = useMemo(() => {
        if (!Cookie.get(GOOGLE_AUTH_ID)) return false;

        const entries = (performance.getEntriesByType('navigation') as PerformanceNavigationTiming[]);

        const isReloadNavigation = entries.map(nav => nav.type)[0] === 'reload';

        if (
            oauthErrorMessage ||
            window.performance && isReloadNavigation ||
            !document.referrer.match(/accounts.google.com|astrolink/g)
        ) {
            return false;
        }

        return true;
    }, [oauthErrorMessage]);

    const initialStateWithProps: IAuthState = {
        ...initialState,

        Container: {
            ...initialState.Container,
            theme,
            uuid: props.uuid,
            origin: props.origin,
            oauthShowConsetTerms,
            listenTo: props.listenTo,
            i18nPayment: props.i18nPayment,
            communication,
            containerColor: customStyle.background,
            buttonGradient: customStyle.buttonGradient,
            isCloseable: props.closeable !== undefined,
            showAppButtons: props.appButtons !== undefined,
            customConfiguration: props.customConfiguration,
            showAstrolinkLogo: props.titleText === undefined,
            disabledButton: props.keepSubmitEnabled == undefined,
            keepSubmitEnabled: props.keepSubmitEnabled !== undefined,
            isThirdPartyLoginEnabled: props.disableThirdParty === undefined,
            allowBottomTermsWarning: props.allowBottomTermsWarning !== undefined,
            step: props.initialStep ? props.initialStep : initialState.Container.initialStep,
            initialStep: props.initialStep ? props.initialStep : initialState.Container.initialStep,
            maintenance: props.maintenance ? true : initialState.Container.maintenance,
            redirectToAuth: props.redirectToAuth ? parseInt(props.redirectToAuth) : 0,

            pagarme: {
                ...initialState.Container.pagarme,
                amount: props.amount ? props.amount : initialState.Container.pagarme.amount,
                planId: props.planId ? props.planId : initialState.Container.pagarme.planId,
                reference: props.reference ? props.reference : initialState.Container.pagarme.reference,
                renewalPlanId: props.planId ? props.renewalPlanId : initialState.Container.pagarme.renewalPlanId,
                urlReference: props.urlReference ? props.urlReference : initialState.Container.pagarme.urlReference
            },

            i18n: {
                ...initialState.Container.i18n,
                dateFormat,
                useUSFormat,
                lang: props.lang
            }
        }
    };

    const [store, dispatch] = useReducer(reducer, initialStateWithProps);

    return {
        store: { ...store },
        dispatch
    };
};

export { IUseCombinedReducersProps };
export default useCombinedReducers;
